import NavBarPrivacy from '../../Components/Landing Page/NavBarPrivacy';
import styles from './PrivacyPolicyPage.module.scss';

const PrivacyPolicyPage = () => {
  return (
      <>
        <NavBarPrivacy/>

        <main className={styles.privacyPolicyWrapper}>
          <div className="container">
            <section id="privacy-policy" className="text-[3rem]">
              <h1>Privacy policy</h1>
              <p>
                We periodically review and update this Privacy Notice to ensure
                ongoing compliance and relevance. The current version was last
                revised on March 11, 2024. ARKITER LTD, a company incorporated
                in England and Wales (Company No. 14065426), has its registered
                office at Foundry Building 2, Smiths Square, 77 Fulham Palace
                Road, London, England, W6 8AF. Throughout this document, the
                terms 'we', 'us', and 'our' refer to ARKITER LTD. As detailed
                herein, our role in the processing of personal information,
                unless stated otherwise, is that of a Data Processor. This
                document outlines our practices regarding the collection,
                holding, and usage of your personal information.
              </p>

              <p>
                This Privacy Notice pertains to our website [www.arkiter.com /
                www.arkiter.io] (the 'Site') and the range of services we
                provide therein. At ARKITER LTD, we specialize in offering
                pre-screening services and facilitating interviews for
                candidates on behalf of our clientele. This policy extends to
                all clients, candidates, and any individuals who interact with
                our Site ('Users'). Typically, we serve as a Data Processor
                under the direction of our clients, adhering strictly to their
                guidelines. For detailed information on how your data is managed
                in such instances, we recommend consulting the privacy policies
                of the respective clients. However, in specific scenarios where
                we function as a Data Controller, we assume full responsibility
                for the management of your data, strictly in alignment with the
                provisions of this Privacy Notice.
              </p>

              <p>
                You can contact us through the following methods:
                <br/>
                Email us at {' '}
                <a href="mailto:privacy@arkiter.com">privacy@arkiter.com</a>
                <br/>
                Send a message via our contact page: {' '}
                <a href="/#contact">https://arkiter.com</a>
              </p>
              <p>
                We prioritize the confidentiality and security of your personal
                information. This privacy notice aims to elucidate our practices
                regarding the collection, storage and use of your personal data.
                We encourage you to read this document thoroughly to understand
                our methods and reasons for processing your information.
              </p>
              <p>
                For any questions or concerns regarding privacy or data
                protection, please contact us at {' '}
                <a href="mailto:privacy@arkiter.com">privacy@arkiter.com</a>.
                Our team is committed to ensuring the security and
                confidentiality of your personal information and is available
                to address your inquiries.
              </p>
              <p>
                We may issue you with other privacy notices from time to time,
                including when we collect personal information from you. This
                privacy notice is intended to supplement these and does not
                override them.
              </p>
            </section>

            <section>
              <h2>1. Key definitions</h2>
              <p>
                For clarity, the key terms that we use throughout this privacy
                notice are defined below:
              </p>
              <p>
                Data Controller: In accordance with UK data protection law, this
                refers to the entity or individual that determines the purposes
                and means of processing personal information.
              </p>
              <p>
                Data Processor: An entity or individual engaged by the Data
                Controller to process personal information based on explicit
                instructions. Responsibilities might include data storage, email
                marketing, or hosting services, all performed under the Data
                Controller's directive.
              </p>
              <p>
                Personal Information: This term denotes any data that can
                identify a living person. Anonymized data, where the identity
                has been permanently removed, is not included under this
                definition.
              </p>
              <p>
                Special Information: This category encompasses highly sensitive
                data that demands additional safeguards as per data protection
                legislation. It includes, but is not limited to, data on health,
                racial and ethnic origins, political opinions, religious
                beliefs, trade union membership, sexual orientation, as well as
                genetic and biometric data.
              </p>
              <p>
                Technical Information: Refers to details on a user's website
                interaction and device specifications, such as IP address,
                browser type, operating system, and device settings. This
                information, collected either directly or via analytics
                providers, helps improve our website and user experience.
              </p>
            </section>

            <section>
              <h2>
                2. Details of personal information that we collect and hold about
                you:
              </h2>
              <p>
                Set out below are the general categories and in each case the
                types of personal information that we collect or may collect,
                use and hold about you:
              </p>
              <ol className="list-decimal">
                <li><strong> Identity information </strong>: This is information
                  relating to your identity such as your name (including any
                  previous names and any titles that you use), gender, marital
                  status and date of birth.
                </li>
                <li><strong> Contact information </strong>: This is information
                  relating to your contact details such as email address,
                  addresses, telephone numbers.
                </li>
                <li><strong> Account information </strong>: This is information
                  relating to your account with us (including username and
                  password).
                </li>
                <li><strong> Payment information </strong>: This category
                  includes details about the methods you use to make payments to
                  us, such as bank account details, credit or debit card
                  details, and the records of transactions between us, including
                  amounts and dates.
                </li>
                <li><strong> Services information </strong>: This is information
                  relating to services and/or digital content provided to you.
                </li>
                <li><strong> Marketing information </strong>: This is
                  information relating to your marketing and communications
                  preferences.
                </li>
                <li><strong> Other information </strong>: Career related
                  information, such as education, job history, any information
                  saved in your account, communications between you and us.
                </li>
                <li><strong> Website, Device and Technical Information </strong>:
                  This is information about your use of our website and
                  technical data which we collect (including your IP address,
                  the type of browser you are using and the version, the
                  operating system you are using, details about the time zone
                  and location settings on the device and other information we
                  receive about your device).
                </li>
                <li><strong> Log File information </strong>: This category
                  encompasses data transmitted by your browser when you visit
                  our Site, such as your computer's Internet Protocol address,
                  browser type and version, the pages of our Site that you
                  visit, the date and time of your visit, the time spent on
                  those pages, and other related statistics.
                </li>
              </ol>
            </section>

            <section>
              <h2>3. Analytics and Remarketing Services:</h2>
              <p>
                We utilize third-party analytics tools to analyze traffic and
                usage patterns on our website. These tools, employing
                cookies—text files stored on your device—gather anonymous data,
                including pages visited and interactions, to enhance site
                functionality. Our use of aggregated analytics ensures
                individual users cannot be identified.
              </p>
              <ol className="list-decimal">
                <li><strong> Google Analytics </strong>: This service places a
                  persistent cookie in your browser to track user interactions.
                  The use of data by Google is governed by their Analytics Terms
                  of Use and Privacy Policy. You can opt-out by disabling
                  cookies or using the Google Analytics Opt-out Browser Add-on.
                </li>
                <li><strong> DoubleClick Cookie by Google </strong>: As a
                  third-party, Google employs DoubleClick cookies to deliver ads
                  based on your visits to our site and others on the internet.
                  Opt-out via Google Ads Settings for more control over
                  interest-based ads.
                </li>
                <li><strong> Remarketing Efforts </strong>: We engage in
                  remarketing to present ads on third-party sites, based on your
                  past visits to our website, using cookies to tailor and
                  optimize ad delivery.
                </li>
                <li><strong> Google Ads </strong>: For ads personalization and
                  opting out, visit Google Ads Settings.
                </li>
                <li><strong> Perfect Audience </strong>: Opt-out options are
                  available at Perfect Audience's Site Opt-out and Partner
                  Opt-out pages. More on their privacy practices can be found on
                  the Perfect Audience Privacy Policy page.
                </li>
                <li><strong> AdRoll </strong>: To opt-out of AdRoll's
                  remarketing, visit their AdRoll Advertising Preferences. Their
                  Privacy Policy offers additional information.
                </li>
                <li><strong> AppNexus </strong>: For opting out of AppNexus
                  remarketing, refer to the AppNexus Privacy page.
                </li>
              </ol>
              <p>
                For comprehensive details on opting out and privacy practices,
                please refer to the respective services' policies.
              </p>
              <p>
                The types of personal data we collect about you may differ from
                person to person, depending on who you are and the relationship
                between us.
              </p>
            </section>

            <section>
              <h2>4. Diversity in Data Collection:</h2>
              <p>
                We recognize the diversity of our users and understand that the
                types of personal data we collect may vary. This variation is
                influenced by the nature of your interaction with our services
                and our relationship with you.

              </p>
            </section>

            <section>
              <h2>5. Children's Privacy: </h2>
              <p>
                Protecting the privacy of young users is especially important to
                us. Our website is not intended for individuals under the age of
                16, and we do not knowingly collect personal data from children
                under this age. Should any parent or guardian discover that
                their child has provided us with their personal information, we
                urge you to reach out to us immediately. Upon learning of any
                such collection without appropriate consent, we are committed to
                taking prompt action to remove the data from our systems.
              </p>
            </section>

            <section>
              <h2>6. Handling of Sensitive Information:</h2>
              <p>
                In alignment with the definitions provided earlier, we take
                extra precautions with sensitive data, which demands higher
                protection levels under data protection laws. We do not collect
                or store any special categories of personal information that
                could reveal sensitive aspects of your identity.

              </p>
            </section>

            <section>
              <h2>7. Use of Personal Information </h2>
              <p>
                We use your personal information in accordance with data
                protection laws, for specific legal reasons outlined below:
              </p>

              <ul className="list-disc">
                <li>
                  <strong>Contractual Necessity</strong>: To fulfill our
                  contractual obligations to you.
                </li>
                <li><strong>Legitimate Interests</strong>: For our legitimate
                  business interests or those of a third party, provided your
                  rights and interests do not override these interests.
                </li>
                <li><strong>Legal Obligations</strong>: To comply with our legal
                  obligations.
                </li>
                <li><strong>Consent</strong>: When you have explicitly consented
                  to the use of your personal information for one or more
                  specific purposes.
                </li>
              </ul>
              <p>
                For certain sensitive personal data requiring additional
                protection, we process it under the following conditions:
              </p>

              <ul className="list-disc">
                <li>With your explicit consent.</li>
                <li>When necessary for legal claims.</li>
                <li>If the data has been made public by you.</li>
              </ul>
              <p>
                Providing us with your personal information is essential for us
                to deliver our services. Without it, we may be unable to serve
                you. Please ensure your personal information is up-to-date, and
                inform us immediately of any changes.
              </p>
              <p>
                Should you wish to withdraw your consent for processing your
                personal information, you can do so at any time by contacting
                us. If you withdraw consent for a specific purpose, we will
                cease processing your data for that purpose unless another legal
                basis applies, in which case we will inform you.
              </p>
              <p>
                Below are the purposes for which we use your personal
                information, along with the corresponding legal bases:
              </p>
              <ul className="list-disc">
                <li><strong>Customer Enrollment</strong>: To provide you with
                  services and/or digital content, based on our contractual
                  necessity and legitimate interests in business development.
                </li>
                <li><strong>Order Processing</strong>: To process transactions,
                  including payments, updates, and any necessary enforcement
                  actions, under contractual necessity and legitimate interests.
                </li>
                <li><strong>Contract Management and Notifications</strong>: To
                  manage our contract and communicate any changes, as required
                  by contractual necessity and legal obligations.
                </li>
                <li><strong>Compliance and Record Keeping</strong>: For auditing
                  and maintaining records, including any guarantees or
                  warranties, as mandated by legal obligations.
                </li>
                <li><strong>Service Improvement</strong>: To enhance the quality
                  of our services and digital content for future customers,
                  driven by our legitimate business interests.
                </li>
                <li><strong>Marketing Communications</strong>: To inform you
                  about relevant services and content, based on our legitimate
                  interests in business growth and your consent.
                </li>
                <li><strong>Website Operation</strong>: To ensure the efficient
                  and secure operation of our website, supported by our
                  legitimate interests.
                </li>
              </ul>
              <p>
                For further details or if you require clarification on the
                specific legal basis we are relying on for processing your
                personal data, please reach out to us.
              </p>
              <p>
                We may anonymize personal information, rendering it
                unidentifiable, for internal uses such as analysis and research.
                Additionally, we might aggregate your data with others' for
                statistical purposes without compromising individual privacy.
              </p>
              <p>
                Per data protection regulations, our use of your personal
                information is confined to the purposes specified to you, except
                where a new purpose aligns with the original ones. Should we
                need to use your data for an incompatible new purpose, we will
                inform you and clarify the lawful basis allowing such use.
              </p>
            </section>

            <section>
              <h2>8. Details of how we collect personal information and special information</h2>
              <p>
                We collect the following information from our clients, who
                request the pre-screening and interview services:
              </p>
              <ul className="list-disc">
                <li><strong>Identity information</strong>: This is information
                  relating to your identity such as your name (including any
                  previous names and any titles that you use), gender, marital
                  status and date of birth.
                </li>
                <li><strong>Contact information</strong>: This is information
                  relating to your contact details such as email address,
                  addresses, telephone numbers.
                </li>
                <li><strong>Account information</strong>: This is information
                  relating to your account with us (including username and
                  password).

                </li>
                <li><strong>Payment information</strong>: Where applicable, this
                  is information relating to the methods by which you provide
                  payment to us such as bank account details, credit or debit
                  card details and details of any payments (including amounts
                  and dates) that are made between us.
                </li>
                <li><strong>Services information</strong>: This is information
                  relating to services and/or digital content provided to you.

                </li>
                <li><strong>Marketing information</strong>: This is information
                  relating to your marketing and communications preferences.

                </li>
                <li><strong>Other information</strong>: Career related
                  information, such as education, job history, any information
                  saved in your account, communications between you and us.
                </li>
              </ul>
              <p>
                We may also collect some of your personal data directly from you
                (e.g. when you fill out a form, survey or questionnaire, contact
                us by email, telephone, in writing or otherwise).
              </p>
              <p>
                We may receive some of your personal information from third
                parties or publicly available sources. This includes:
              </p>
              <p>
                Identity Information and Contact Information from publicly
                available sources such as Companies House and others;
              </p>
              <p>
                Website, Device and Technical Information from third parties
                such as analytics providers (such as Google);
              </p>
              <p>
                We may also receive Website, Device and Technical Information
                automatically from technologies such as cookies that are
                installed on our website. To find out more about these please
                see our cookie policy, which is available here.
              </p>
            </section>

            <section>
              <h2>
                9. Details about with whom personal information may be shared:
              </h2>
              <p>
                We may need to share your personal information with other
                organizations or individuals. These entities include:
              </p>
              <ul className="list-disc">
                <li>Other companies within our group, which may serve as joint
                  data controllers or data processors. These companies require
                  access to personal information for services such as IT and may
                  share data for purposes like management reporting.
                </li>
                <li>Third parties outside our group, which may include:
                  <ul className="list-disc">
                    <li>Suppliers, such as providers of IT support services,
                      payment processing, administrative services, and marketing
                      agencies, located in various countries worldwide.
                    </li>
                    <li>Government and regulatory bodies, such as HMRC and fraud
                      prevention agencies, also based in multiple countries.
                    </li>
                    <li>Our professional advisors, including lawyers,
                      accountants, auditors, and insurance companies, located
                      internationally.
                    </li>
                  </ul>
                </li>
                <li>Our banking partners and credit reference agencies.
                </li>
                <li>Email service providers.
                </li>
                <li>Any organizations considering the acquisition of our
                  business and assets, to whom we may disclose your personal
                  information.
                </li>
              </ul>
              <p>
                We ensure that any sharing of personal information with Data
                Processors is governed by contractual agreements that specify
                both parties' responsibilities, especially concerning data
                security.
              </p>
              <p>
                We do not sell or trade any personal information that you have
                provided to us.
              </p>
            </section>

            <section>
              <h2>
                10. International Transfers of Personal Information
              </h2>
              <p>
                When transferring personal information outside the European
                Economic Area (EEA), we commit to maintaining a protection level
                comparable to EEA standards. This compliance aligns with data
                protection laws and considers available legal exceptions for
                international data transfers.
              </p>
              <p>
                Such transfers may be necessary to entities within our corporate
                group or third-party organizations located outside the EEA, as
                described in the previous sections of this policy.
              </p>
              <p>To safeguard your personal information, we adhere to the
                following measures:
              </p>
              <ul className="list-disc">
                <li>Transferring data only to countries recognized by the
                  European Commission as providing adequate data protection.
                </li>
                <li>Applying binding corporate rules within our group for
                  international data transfers.
                </li>
                <li>Utilizing standard contractual clauses approved by the
                  European Commission.
                </li>
                <li>Following codes of conduct approved by relevant data
                  protection authorities, like the Information Commissioner’s
                  Office (ICO) in the UK.
                </li>
                <li>Engaging in certification mechanisms to ensure data
                  protection.
                </li>
              </ul>

              <p>Please note that the EU-US Privacy Shield is no longer a valid
                mechanism for EEA-US data transfers following recent legal
                developments.
              </p>
            </section>

            <section>
              <h2>
                11. Retention of Personal Information
              </h2>
              <p>
                We retain personal information only as necessary, based on the
                purpose of data collection and legal requirements, such as those
                related to accounting, auditing, and taxation. This is also to
                protect against potential legal claims or to uphold any
                guarantees or warranties associated with our services.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Candidates' Information:</strong> Names, email
                addresses, and test scores are kept for 5 years.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Webcam Images:</strong> Images captured during
                interviews are stored for 6 months.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Video Recordings:</strong> Interviews are archived for 2
                years.
              </p>
            </section>

            <section>
              <h2>
                12. Anonymized Data Retention:
              </h2>
              <p>
                In our commitment to continuous improvement and to enhance
                platform functionality, we anonymize and indefinitely retain
                data related to candidate interactions with our platform. This
                anonymization process rigorously removes all personal
                identifiers, ensuring the impossibility of tracing data back to
                any individual. The anonymized data serves as a valuable
                resource for analytical insights, feature development, and
                service optimization, all while strictly adhering to data
                protection principles.
              </p>
            </section>

            <section>
              <h2>
                13. Candidate Feedback and Consent:
              </h2>
              <p>
                In addition to the data retention policies outlined above, we
                also value the feedback provided by candidates regarding their
                experience with our platform. This feedback is crucial for our
                ongoing improvement and to ensure the effectiveness of our
                services.
              </p>
              <ul className="list-disc">
                <li>Feedback Collection: At the conclusion of the interview
                  process, candidates may be invited to participate in a survey
                  to share their experience. Participation is entirely
                  voluntary, and any personal information provided in feedback
                  is processed and protected with the same level of care as all
                  other candidate data.
                </li>
                <li>Data Use and Consent: While our primary service agreements
                  are with our hiring clients, the collection of candidate
                  feedback directly from candidates involves a separate consent
                  process. By participating in the feedback survey, candidates
                  expressly consent to the use of their responses for service
                  improvement purposes. This consent is distinct from any
                  agreements or understandings with our clients and does not
                  impact the candidates' rights or our obligations under data
                  protection laws.
                </li>
                <li>Anonymity and Use: Whenever possible, feedback data is
                  anonymized and aggregated for analysis. If specific feedback
                  requires addressing or follow-up, any personal identifiers are
                  used solely for that purpose and are handled according to the
                  strict privacy standards set forth in this notice.
                </li>
              </ul>
              <p>
                We are dedicated to ensuring transparency and respect for
                privacy in all interactions. If candidates or clients have any
                concerns or questions about the use of feedback data or any
                other privacy matters, we encourage direct communication through
                the channels provided in this notice.
              </p>
            </section>

            <section>
              <h2>
                14. Automated decision making
              </h2>
              <p>
                ‘Automated decision making’ involves decisions made
                automatically, without human involvement. Under data protection
                laws, this includes profiling. ‘Profiling’ is the automated
                processing of personal data to evaluate or analyse certain
                personal aspects of a person (such as their behaviour,
                characteristics, interests and preferences).
              </p>
            </section>

            <section>
              <h2>
                15. Your Rights Under Data Protection Law
              </h2>
              <p>
                You are entitled to various rights under data protection
                legislation concerning your personal information:
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Right to Access:</strong> Often referred to as 'subject
                access,' this
                right enables you to receive a copy of the personal information
                we hold about you and to understand how it is used.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Right to Correction:</strong> You can request the
                correction of
                inaccurate personal information and completion of incomplete
                data.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Right to Erasure:</strong> Also known as the 'right to
                be forgotten,'
                this applies in specific contexts, allowing you to request the
                deletion of your personal information.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Right to Restrict Processing:</strong> In certain
                situations, you have
                the right to request a halt on the processing of your personal
                information.
              </p>
              <p style={{marginBottom: "1px"}}>
                <strong>Right to Data Portability:</strong> This right allows
                you to move, copy,
                or transfer personal information easily from one IT environment
                to another.
              </p>
              <p>
                <strong>Right to Object:</strong> You may object to the
                processing of your
                personal information for direct marketing or when processing is
                based on legitimate interests. This includes objecting to
                profiling activities related to direct marketing.
              </p>

              <p>
                Should you choose to withdraw consent for processing your
                personal information, you are at liberty to do so at any time.
              </p>
              <p>
                To exercise your rights, please reach out to us via the contact
                details provided. Note that we may require specific information
                to confirm your identity for security purposes. There is no fee
                for exercising your rights, except in cases where requests are
                deemed unfounded or excessive, at which point we may charge a
                nominal fee or decline the request.
              </p>
            </section>

            <section>
              <h2>
                16. Marketing Communications
              </h2>
              <p>You may receive marketing communications from us related to
                services similar to those you've engaged with, provided you've
                given consent or under circumstances where we are legally
                permitted to do so.
              </p>
              <p>We ensure you have control over these communications. Every
                marketing email includes options to unsubscribe, opt-out, or
                adjust your preferences. Additionally, if you hold an account
                with us, you may directly manage your marketing preferences
                within your account settings. For further adjustments to your
                marketing preferences or to cease all marketing communications,
                please reach out to us using the contact information provided at
                the start of this document.
              </p>
              <p>Please note, opting out of marketing communications does not
                exclude you from receiving essential service-related
                information, such as updates on purchases or changes to our
                services. Your personal information is not shared with third
                parties for their marketing purposes.
              </p>
            </section>

            <section>
              <h2>
                17. Complaints
              </h2>
              <p>
                If you have concerns about how we've handled your personal
                information, you're entitled to lodge a complaint with the
                Information Commissioner’s Office (ICO), the UK's data
                protection authority. We encourage you to contact us as we're
                committed to resolving any issues directly and value the
                opportunity to address any concerns. Please reach out to us
                using the contact information provided at the start of this
                privacy notice.
              </p>
            </section>

            <section>
              <h2>
                18. Third-party websites
              </h2>
              <p>
                Our website may contain links to third-party websites. Clicking
                on these links will redirect you to external sites. It's
                important to note that these third-party sites may collect your
                personal information. We advise you to review their privacy
                policies to understand how they handle your data.
              </p>
            </section>

          </div>
        </main>
      </>

  );
};

export default PrivacyPolicyPage;
